/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import * as Plotly from 'plotly.js-dist-min';
import { Config, Data, Layout } from 'plotly.js-dist-min';
import { fromEvent, debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ExperimentData,
  IDevice,
  IFlask,
  ITimeValue,
} from 'src/app/_models/experiment-data';
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { FormBuilder, Validators } from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { UnderlinedDropdownFormFieldComponent } from '../../forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../forms/underlined-form-field/underlined-form-field.component';
import { Device } from 'src/app/_models/device';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';
import { AdvancedSettings } from 'src/app/_models/advanced-settings';
import { SlideToggleGroupComponent } from '../../selection-controls/slide-toggle-group/slide-toggle-group.component';

@Component({
  selector: 'app-oxygen-experiment-graph',
  templateUrl: './oxygen-experiment-graph.component.html',
  styleUrls: ['./oxygen-experiment-graph.component.scss'],
  imports: [
    MatSlideToggle,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    UnderlinedFormFieldComponent,
    MatIconButtonComponent,
    SlideToggleGroupComponent,
  ],
})
export class OxygenExperimentGraphComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  readonly plotlyDiv = viewChild<ElementRef>('plotlyDiv');

  private readonly destroy$ = new Subject<void>();
  protected formBuilder = inject(FormBuilder);

  // Colour scale - same as D3 version
  private readonly coloursList: string[][] = [
    ['#6ECF9C', '#6C73C1', '#CE517E', '#37494A'],
    ['#F03E2B', '#BE4FC1', '#CB9F50', '#7FCE4C'],
    ['#5BB4E9', '#FFA62A', '#969696', '#D6A7D7'],
    ['#346684', '#496A35', '#553056', '#AA6E5C'],
  ];

  checked = signal(false);
  dragMode = signal<'zoom' | 'pan'>('zoom');
  graphMode = signal<'markers' | 'lines+markers'>('markers');
  private graphReady = false;
  errorMessage: string | null = null;
  private readonly margin = {
    left: 70,
    right: 5,
    top: 10,
    bottom: 60,
  };

  private width = 800;
  private height = 450;
  private readonly maxHeight = 450;

  // Point magnification settings
  private readonly minMarkerSize = 2;
  private readonly maxMarkerSize = 12;
  private baseMarkerSize = 8;
  private initialXRange: [number, number] | null = null;
  private initialYRange: [number, number] | null = null;

  dataToRender: IRenderData[] = [];
  filteredDataToRender: IRenderData[] = [];
  private labelList: IPropertyColour[] = [];
  legendData = signal<ILegendDevice[]>([]);

  // Oxygen-specific filter state
  dataFilter = signal<string>('both');
  flaskFilter = signal<string>('airSaturation');

  // Form variables
  minGraphHeight = 200;
  minGraphWidth = 200;
  advancedSettings: AdvancedSettings;

  // Signal inputs
  readonly graphId = input('oxygen-scatter');
  readonly yAxisLabel = input('Oxygen [%]');
  readonly experimentData = input<ExperimentData>();
  readonly devices = input<Device[]>();
  readonly experimentName = input<string>();

  private previousExperimentData: ExperimentData | undefined;

  widthField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-width`,
      label: 'Width (px)',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  heightField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-height`,
      label: 'Height (px)',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  yMinRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-yMinRange`,
      label: 'y Min',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  yMaxRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-yMaxRange`,
      label: 'y Max',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  xMinRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-xMinRange`,
      label: 'x Min',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  xMaxRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-xMaxRange`,
      label: 'x Max',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  ratioField = computed(
    () =>
      new FormBase({
        key: `${this.graphId()}-ratio`,
        label: 'Aspect Ratio',
        type: 'dropdown',
        placeholder: '',
        disabled: false,
        required: false,
        value: 'default',
        options: [
          { key: 'default', value: '1.8:1' },
          { key: 'square', value: '1:1' },
          { key: 'threeByTwo', value: '3:2' },
        ],
      }),
  );

  oxygenDataFlaskFilterField = computed(
    () =>
      new FormBase({
        key: `${this.graphId()}-oxygenDataFlaskFilter`,
        label: 'Oxygen data flask filter',
        type: 'toggle',
        placeholder: 'Select data',
        disabled: false,
        required: false,
        value: 'airSaturation',
        options: [
          { key: 'airSaturation', value: 'Air Saturation %' },
          { key: 'mBar', value: 'Millibar' },
          { key: 'uMolar', value: 'Micromolar' },
        ],
      }),
  );

  oxygenDataFilterField = computed(
    () =>
      new FormBase({
        key: `${this.graphId()}-oxygenDataFilter`,
        label: 'Oxygen data filter',
        type: 'toggle',
        placeholder: 'Select data',
        disabled: false,
        required: false,
        value: 'both',
        options: [
          { key: 'early', value: 'Early' },
          { key: 'late', value: 'Late' },
          { key: 'both', value: 'Both' },
        ],
      }),
  );

  advancedSettingsForm = computed(() =>
    this.formBuilder.group({
      [this.widthField().key]: [
        this.widthField().value,
        Validators.compose([
          Validators.required,
          Validators.min(this.minGraphWidth),
        ]),
      ],
      [this.heightField().key]: [
        this.heightField().value,
        Validators.compose([
          Validators.required,
          Validators.min(this.minGraphHeight),
        ]),
      ],
      [this.yMinRangeField().key]: [this.yMinRangeField().value, Validators.required],
      [this.yMaxRangeField().key]: [
        this.yMaxRangeField().value,
        [Validators.required],
      ],
      [this.xMinRangeField().key]: [
        this.xMinRangeField().value,
        [Validators.required],
      ],
      [this.xMaxRangeField().key]: [
        this.xMaxRangeField().value,
        [Validators.required],
      ],
      [this.ratioField().key]: [this.ratioField().value],
      [this.oxygenDataFlaskFilterField().key]: [this.oxygenDataFlaskFilterField().value],
      [this.oxygenDataFilterField().key]: [this.oxygenDataFilterField().value],
    }),
  );

  constructor() {
    // Effect to react to experimentData changes
    effect(() => {
      const data = this.experimentData();
      if (
        data &&
        this.previousExperimentData &&
        data !== this.previousExperimentData
      ) {
        this.dataToRender = [];
        this.labelList = [];
        this.parseData(data, true);
      }
      this.previousExperimentData = data;
    });
  }

  ngAfterViewInit(): void {
    this.initialiseGraph();
  }

  private readonly initialiseGraph = (): void => {
    const data = this.experimentData();
    if (data) {
      this.parseData(data);
    }

    // Initialize filter values
    this.dataFilter.set(
      (this.advancedSettingsForm().get(this.oxygenDataFilterField().key)?.value as string) || 'both'
    );
    this.flaskFilter.set(
      (this.advancedSettingsForm().get(this.oxygenDataFlaskFilterField().key)?.value as string) || 'airSaturation'
    );

    // Resize observer
    const resizeObservable$ = fromEvent(window, 'resize').pipe(
      debounceTime(300),
    );

    resizeObservable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleResize();
    });

    // Flask filter changes
    this.advancedSettingsForm()
      .get(this.oxygenDataFlaskFilterField().key)
      ?.valueChanges.subscribe((value: string) => {
        this.flaskFilter.set(value);
        this.applyFiltersAndUpdate();
      });

    // Data filter changes (early/late/both)
    this.advancedSettingsForm()
      .get(this.oxygenDataFilterField().key)
      ?.valueChanges.subscribe((value: string) => {
        this.dataFilter.set(value);
        this.applyFiltersAndUpdate();
      });

    // Form value changes
    this.advancedSettingsForm()
      .get(this.ratioField().key)
      ?.valueChanges.subscribe((value) => {
        this.updateDimensions(value);
      });

    this.advancedSettingsForm()
      .get(this.xMinRangeField().key)
      ?.valueChanges.subscribe((value) => {
        this.advancedSettingsForm().controls[
          this.xMaxRangeField().key
        ].setValidators([Validators.required, Validators.min(value)]);
        this.advancedSettingsForm().controls[
          this.xMaxRangeField().key
        ].updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });

    this.advancedSettingsForm()
      .get(this.yMinRangeField().key)
      ?.valueChanges.subscribe((value) => {
        this.advancedSettingsForm().controls[
          this.yMaxRangeField().key
        ].setValidators([Validators.required, Validators.min(value)]);
        this.advancedSettingsForm().controls[
          this.yMaxRangeField().key
        ].updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });
  };

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      simpleChanges['experimentData'] &&
      !simpleChanges['experimentData'].firstChange
    ) {
      this.dataToRender = [];
      this.labelList = [];
      this.parseData(this.experimentData(), true);
    }
  }

  /**
   * Apply current filters and update the graph
   */
  private applyFiltersAndUpdate(): void {
    this.filteredDataToRender = this.filterData();
    this.updateGraph();
  }

  /**
   * Filter data based on current flask and data filters
   */
  filterData(): IRenderData[] {
    const flaskFilterValue = this.flaskFilter();
    const dataFilterValue = this.dataFilter();

    const filteredFlaskList = this.dataToRender.filter((d) => {
      const flaskName = d.FlaskName.toLowerCase();
      return flaskName.includes(flaskFilterValue.toLowerCase());
    });

    const filteredDataList = filteredFlaskList.filter((d) => {
      if (dataFilterValue === 'early') {
        return d.IsEarly;
      } else if (dataFilterValue === 'late') {
        return !d.IsEarly;
      }
      return true; // For 'both' or any other case
    });

    return filteredDataList;
  }

  /**
   * Get the Y axis label based on current flask filter
   */
  private getYAxisLabel(): string {
    const flaskFilterValue = this.flaskFilter();
    if (flaskFilterValue === 'airSaturation') {
      return 'Oxygen [%]';
    } else if (flaskFilterValue === 'mBar') {
      return 'Oxygen [mBar]';
    } else {
      return 'Oxygen [μM]';
    }
  }

  /**
   * Safely execute Plotly operations with error handling
   */
  private safePlotlyOperation(operation: () => void, errorMsg: string): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) {
      return;
    }
    try {
      operation();
    } catch (error) {
      this.errorMessage = errorMsg;
    }
  }

  /**
   * Calculate zoom scale and update marker sizes
   */
  private updateMarkerSizeOnZoom(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement || !this.initialXRange || !this.initialYRange) {
      return;
    }

    const layout = this.plotlyDiv().nativeElement.layout;
    const currentXRange = layout.xaxis?.range;
    const currentYRange = layout.yaxis?.range;

    if (!currentXRange || !currentYRange) return;

    const initialXSpan = this.initialXRange[1] - this.initialXRange[0];
    const initialYSpan = this.initialYRange[1] - this.initialYRange[0];
    const currentXSpan = currentXRange[1] - currentXRange[0];
    const currentYSpan = currentYRange[1] - currentYRange[0];

    const xZoomScale = initialXSpan / currentXSpan;
    const yZoomScale = initialYSpan / currentYSpan;
    const zoomScale = (xZoomScale + yZoomScale) / 2;

    const scaledSize = this.baseMarkerSize * zoomScale;
    const newSize = Math.min(
      Math.max(this.minMarkerSize, scaledSize),
      this.maxMarkerSize
    );

    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      try {
        // Group traces by symbol type for efficient restyling
        const traces = this.plotlyDiv().nativeElement.data;
        const triangleIndices: number[] = [];
        const squareIndices: number[] = [];

        for (let i = 0; i < traceCount; i++) {
          if (traces[i].marker?.symbol === 'triangle-up') {
            triangleIndices.push(i);
          } else {
            squareIndices.push(i);
          }
        }

        // Apply scaled size to triangles
        if (triangleIndices.length > 0) {
          Plotly.restyle(this.plotlyDiv().nativeElement, { 'marker.size': newSize * 1.3 }, triangleIndices);
        }
        // Apply base size to squares
        if (squareIndices.length > 0) {
          Plotly.restyle(this.plotlyDiv().nativeElement, { 'marker.size': newSize }, squareIndices);
        }
      } catch (error) {
        // Silently fail - marker size update is not critical
      }
    }
  }

  /**
   * Clear any active tooltips/hover state
   */
  private clearTooltips(): void {
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.Fx.hover(this.plotlyDiv().nativeElement, []);
      } catch (error) {
        // Silently fail
      }
    }
  }

  toggleChange(event: MatSlideToggleChange): void {
    this.checked.set(event.checked);

    if (!this.graphReady) {
      return;
    }

    const currentLayout = this.plotlyDiv()?.nativeElement?.layout;
    const currentXRange = currentLayout.xaxis?.range;
    const currentYRange = currentLayout.yaxis?.range;

    const update: Partial<Layout> = {
      xaxis: {
        ...this.getXAxisConfig(),
        range: currentXRange,
        fixedrange: this.checked(),
      },
      yaxis: {
        ...this.getYAxisConfig(),
        range: currentYRange,
        fixedrange: this.checked(),
      },
    };

    this.safePlotlyOperation(
      () => Plotly.relayout(this.plotlyDiv().nativeElement, update),
      'Failed to toggle zoom. Try again'
    );
  }

  resetZoom(): void {
    const { minY, maxY, minX, maxX } = this.getDataBounds(this.filteredDataToRender);

    const xVal = (maxX - minX) * 0.05;
    const yVal = (maxY - minY) * 0.05;

    const totalPoints = this.filteredDataToRender.length;
    const useWebGL = totalPoints > 5000;

    if (this.advancedSettings) {
      this.advancedSettingsForm().patchValue({
        [this.widthField().key]: this.advancedSettings.width.toString(),
        [this.heightField().key]: this.advancedSettings.height.toString(),
        [this.yMinRangeField().key]: this.advancedSettings.yMin.toString(),
        [this.yMaxRangeField().key]: this.advancedSettings.yMax.toString(),
        [this.xMinRangeField().key]: this.advancedSettings.xMin.toString(),
        [this.xMaxRangeField().key]: this.advancedSettings.xMax.toString(),
        [this.ratioField().key]: this.advancedSettings.aspectRatio,
      });
    }

    const update: Partial<Layout> = {
      'xaxis.range': [minX - xVal, maxX + xVal],
      'yaxis.range': [minY - yVal, maxY + yVal],
    };

    if (useWebGL) {
      this.safePlotlyOperation(
        () => {
          Plotly.relayout(this.plotlyDiv().nativeElement, update);
          this.clearTooltips();
        },
        'Failed to reset zoom. Please try again.'
      );
    } else {
      this.safePlotlyOperation(
        () => {
          Plotly.animate(
            this.plotlyDiv().nativeElement,
            { layout: update },
            {
              transition: {
                duration: 750,
                easing: 'cubic-in-out',
              },
              frame: {
                duration: 750,
              },
            },
          );
          this.clearTooltips();
        },
        'Failed to reset zoom. Please try again.'
      );
    }
  }

  zoomIn(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = (xRange[1] - xRange[0]) / 4;
    const yHalf = (yRange[1] - yRange[0]) / 4;

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
          'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
        });
        this.clearTooltips();
      },
      'Failed to zoom in. Please try again.'
    );
  }

  zoomOut(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = xRange[1] - xRange[0];
    const yHalf = yRange[1] - yRange[0];

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
          'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
        });
        this.clearTooltips();
      },
      'Failed to zoom out. Please try again.'
    );
  }

  setDragMode(mode: 'zoom' | 'pan'): void {
    this.dragMode.set(mode);
    this.safePlotlyOperation(
      () => Plotly.relayout(this.plotlyDiv().nativeElement, { dragmode: mode }),
      'Failed to change drag mode. Please try again.'
    );
  }

  setGraphMode(mode: 'markers' | 'lines+markers'): void {
    this.graphMode.set(mode);

    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      const traceIndices = Array.from({ length: traceCount }, (_, i) => i);
      this.safePlotlyOperation(
        () => Plotly.restyle(this.plotlyDiv().nativeElement, { mode }, traceIndices),
        'Failed to change graph mode. Please try again.'
      );
    }
  }

  downloadImage(): void {
    this.safePlotlyOperation(
      () => Plotly.downloadImage(this.plotlyDiv().nativeElement, {
        format: 'png',
        filename: `${this.experimentName()} - Oxygen`,
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      }),
      'Failed to download image. Please try again.'
    );
  }

  resizeGraph(): void {
    const width = parseInt(this.advancedSettingsForm().controls[this.widthField().key]?.value);
    const height = parseInt(
      this.advancedSettingsForm().controls[this.heightField().key]?.value,
    );
    const xMin = parseFloat(
      this.advancedSettingsForm().controls[this.xMinRangeField().key]?.value,
    );
    const xMax = parseFloat(
      this.advancedSettingsForm().controls[this.xMaxRangeField().key]?.value,
    );
    const yMin = parseFloat(
      this.advancedSettingsForm().controls[this.yMinRangeField().key]?.value,
    );
    const yMax = parseFloat(
      this.advancedSettingsForm().controls[this.yMaxRangeField().key]?.value,
    );

    if (isNaN(width) || isNaN(height) || isNaN(xMin) || isNaN(xMax) || isNaN(yMin) || isNaN(yMax)) {
      this.errorMessage = 'Invalid dimensions provided. Please check all input values are valid numbers.';
      return;
    }

    if (width <= 0 || height <= 0) {
      this.errorMessage = 'Width and height must be greater than zero.';
      return;
    }

    if (xMin >= xMax) {
      this.errorMessage = 'X-axis minimum must be less than maximum.';
      return;
    }

    if (yMin >= yMax) {
      this.errorMessage = 'Y-axis minimum must be less than maximum.';
      return;
    }

    const xVal = (xMax - xMin) * 0.05;
    const yVal = (yMax - yMin) * 0.05;

    this.width = width;
    this.height = height;

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          width: width + this.margin.left + this.margin.right,
          height: height + this.margin.top + this.margin.bottom,
          'xaxis.range': [xMin - xVal, xMax + xVal],
          'yaxis.range': [yMin - yVal, yMax + yVal],
        });
        this.clearTooltips();
      },
      'Failed to resize graph. Please check your dimensions and try again.'
    );
  }

  updateSizeFormValues(): void {
    this.advancedSettingsForm().patchValue({
      [this.widthField().key]: this.width.toFixed(2).toString(),
      [this.heightField().key]: this.height.toFixed(2).toString(),
    });
  }

  private handleResize(): void {
    const value =
      this.advancedSettingsForm().controls[this.ratioField().key]?.value || 'default';
    this.updateDimensions(value);
  }

  private updateDimensions(ratio?: string): void {
    setTimeout(() => {
      if (!this.plotlyDiv()?.nativeElement) return;

      const rect = this.plotlyDiv()!.nativeElement.getBoundingClientRect();
      const containerWidth = rect.width > 0 ? rect.width : 800;

      const maxWidth = containerWidth - (this.margin.left + this.margin.right);

      if (!ratio || ratio === 'default') {
        this.width = maxWidth;
        const desiredHeight = maxWidth / 1.8;
        this.height =
          desiredHeight < this.maxHeight ? desiredHeight : this.maxHeight;
      } else if (ratio === 'square') {
        const size = Math.min(maxWidth, this.maxHeight);
        this.width = size;
        this.height = size;
      } else if (ratio === 'threeByTwo') {
        const desiredHeight = maxWidth / 1.5;
        if (desiredHeight <= this.maxHeight) {
          this.width = maxWidth;
          this.height = desiredHeight;
        } else {
          this.height = this.maxHeight;
          this.width = this.maxHeight * 1.5;
        }
      }

      this.updateSizeFormValues();

      const newLayout = {
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      };

      this.safePlotlyOperation(
        () => {
          Plotly.relayout(this.plotlyDiv().nativeElement, newLayout);
          this.clearTooltips();
        },
        'Failed to update graph dimensions. Please try again.'
      );
    }, 0);
  }

  private readonly parseData = (
    experimentData: ExperimentData,
    isUpdate = false,
  ): void => {
    const property = experimentData.dataType;
    const deviceList: IDevice[] = experimentData.devices;

    deviceList?.forEach((dataSet, index) => {
      const validFlasks = dataSet.flasks.filter(
        (flask) => flask && Object.keys(flask).length > 0 && flask.name && flask.values,
      );

      validFlasks.forEach((flask: IFlask) => {
        const deviceFlaskProperty = `${dataSet.deviceId}_${flask.name}_${property}`;

        if (
          flask.values &&
          Array.isArray(flask.values) &&
          flask.values.length > 0
        ) {
          flask.values.forEach((timeValue: ITimeValue) => {
            if (
              timeValue &&
              typeof timeValue.time === 'number' &&
              typeof timeValue.value === 'number'
            ) {
              const y = timeValue.value;
              const x = timeValue.time;

              this.dataToRender.push({
                DeviceFlaskProperty: deviceFlaskProperty,
                Time: x,
                Value: y,
                DeviceName: dataSet.deviceId.toString(),
                FlaskName: flask.name,
                IsEarly: timeValue.isEarly,
              });
            }
          });

          const colourIndex = this.colourIndex(flask.name);

          this.labelList.push({
            deviceFlaskProperty,
            colour: this.coloursList[index][colourIndex],
          });
        }
      });

      if (experimentData.devices.length === index + 1) {
        // Apply initial filters
        this.filteredDataToRender = this.filterData();

        if (isUpdate) {
          this.updateGraph();
        } else {
          this.drawGraph();
        }
      }
    });
  };

  colourIndex(property: string): number {
    const lastChar = property[property.length - 1];
    switch (lastChar) {
      case 'A':
        return 0;
      case 'B':
        return 1;
      case 'C':
        return 2;
      case 'D':
        return 3;
      default:
        return 0;
    }
  }

  /**
   * Efficiently calculate min/max bounds
   */
  private getDataBounds(data: IRenderData[]): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    if (data.length === 0) {
      return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < data.length; i++) {
      const point = data[i];
      if (point.Time < minX) minX = point.Time;
      if (point.Time > maxX) maxX = point.Time;
      if (point.Value < minY) minY = point.Value;
      if (point.Value > maxY) maxY = point.Value;
    }

    return { minX, maxX, minY, maxY };
  }

  private getXAxisConfig(): Partial<Layout['xaxis']> {
    const bounds = this.getDataBounds(this.filteredDataToRender);
    const minX =
      parseFloat(this.advancedSettingsForm().controls[this.xMinRangeField().key]?.value) ||
      bounds.minX;
    const maxX =
      parseFloat(this.advancedSettingsForm().controls[this.xMaxRangeField().key]?.value) ||
      bounds.maxX;
    const xVal = (maxX - minX) * 0.05;

    return {
      title: {
        text: 'Time [h]',
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      range: [minX - xVal, maxX + xVal],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      nticks: 10,
      tickmode: 'auto',
    };
  }

  private getYAxisConfig(): Partial<Layout['yaxis']> {
    const bounds = this.getDataBounds(this.filteredDataToRender);
    const minY =
      parseFloat(this.advancedSettingsForm().controls[this.yMinRangeField().key]?.value) ||
      bounds.minY;
    const maxY =
      parseFloat(this.advancedSettingsForm().controls[this.yMaxRangeField().key]?.value) ||
      bounds.maxY;
    const yVal = (maxY - minY) * 0.05;

    return {
      title: {
        text: this.getYAxisLabel(),
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      type: 'linear',
      range: [minY - yVal, maxY + yVal],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      nticks: 10,
      tickmode: 'auto',
    };
  }

  /**
   * Get Plotly symbol based on IsEarly property
   * Early = triangle-up, Late = square
   */
  private getSymbol(isEarly: boolean): string {
    return isEarly ? 'triangle-up' : 'square';
  }

  /**
   * Get marker size adjusted for symbol type
   * Triangles need to be larger to appear visually similar to squares
   */
  private getMarkerSize(baseSize: number, isEarly: boolean): number {
    // Scale triangle up by ~1.3 to match square visual area
    return isEarly ? baseSize * 1.3 : baseSize;
  }

  private readonly drawGraph = (): void => {
    const { minY, maxY, minX, maxX } = this.getDataBounds(this.filteredDataToRender);

    const roundedMinY = parseFloat(minY.toFixed(4)) || 0;
    let roundedMaxY = parseFloat(maxY.toFixed(4)) || 1;
    const roundedMinX = parseFloat(minX.toFixed(4)) || 0;
    let roundedMaxX = parseFloat(maxX.toFixed(4)) || 1;

    if (roundedMinX === roundedMaxX) {
      roundedMaxX = roundedMinX + 1;
    }
    if (roundedMinY === roundedMaxY) {
      roundedMaxY = roundedMinY + 1;
    }

    this.advancedSettingsForm().patchValue({
      [this.xMinRangeField().key]: roundedMinX.toString(),
      [this.xMaxRangeField().key]: roundedMaxX.toString(),
      [this.yMinRangeField().key]: roundedMinY.toString(),
      [this.yMaxRangeField().key]: roundedMaxY.toString(),
    });

    // Calculate dimensions
    const containerWidth = this.plotlyDiv()?.nativeElement?.offsetWidth || 800;
    this.width = containerWidth - (this.margin.left + this.margin.right);
    const currentHeight = this.width / 1.8;
    this.height =
      currentHeight < this.maxHeight ? currentHeight : this.maxHeight;
    this.updateSizeFormValues();

    // Set default form values
    this.advancedSettings = new AdvancedSettings(
      roundedMinX,
      roundedMaxX,
      roundedMinY,
      roundedMaxY,
      parseInt(this.height.toFixed(2)),
      parseInt(this.width.toFixed(2)),
      this.ratioField().value,
    );

    // Group data by DeviceFlaskProperty and IsEarly
    const groupedData = new Map<string, IRenderData[]>();
    this.filteredDataToRender.forEach((point) => {
      // Create a unique key that includes IsEarly status for separate traces
      const key = `${point.DeviceFlaskProperty}_${point.IsEarly ? 'early' : 'late'}`;
      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key)!.push(point);
    });

    // Determine if we should use WebGL
    const totalPoints = this.filteredDataToRender.length;
    const useWebGL = totalPoints > 5000;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    // Optimise marker size for very large datasets
    const markerSize = totalPoints > 50000 ? 4 : totalPoints > 10000 ? 6 : 8;
    this.baseMarkerSize = markerSize;

    // Create traces and build custom legend data
    const seenDevices = new Set<string>();
    const traces: Data[] = [];
    const legendItems: ILegendDevice[] = [];
    const deviceMap = new Map<string, ILegendDevice>();

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const baseKey = key.replace(/_early$|_late$/, '');
      const label = this.labelList.find((l) => l.deviceFlaskProperty === baseKey);
      const color = label?.colour || '#000000';
      const deviceId = points[0]?.DeviceName || 'Unknown Device';
      const device = this.devices()?.find((d) => d.id.toString() === deviceId);
      const deviceName = device?.name || deviceId;
      const flaskName = points[0]?.FlaskName || key;
      const isEarly = points[0]?.IsEarly;

      // Track device for legend
      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const legendDevice: ILegendDevice = {
          deviceId,
          deviceName,
          flasks: [],
        };
        deviceMap.set(deviceId, legendDevice);
        legendItems.push(legendDevice);
      }

      const traceIndex = traces.length;

      // Add flask trace with appropriate symbol
      traces.push({
        x: points.map((p) => p.Time),
        y: points.map((p) => p.Value),
        mode: this.graphMode(),
        type: traceType,
        name: `${flaskName} (${isEarly ? 'Early' : 'Late'})`,
        marker: {
          color: color,
          size: this.getMarkerSize(markerSize, isEarly),
          symbol: this.getSymbol(isEarly),
        },
        line: {
          color: color,
          width: 1,
        },
        hovertemplate:
          '<b>Device:</b> %{customdata[0]}<br>' +
          '<b>Flask:</b> %{customdata[1]}<br>' +
          '<b>Type:</b> %{customdata[2]}<br>' +
          '<b>Time:</b> %{x}<br>' +
          '<b>Value:</b> %{y}' +
          '<extra></extra>',
        customdata: points.map((p) => [p.DeviceName, p.FlaskName, p.IsEarly ? 'Early' : 'Late']),
      } as Data);

      // Add to legend data (only add unique flask names, not duplicates for early/late)
      const existingFlask = deviceMap.get(deviceId)?.flasks.find(f => f.name === `O2 ${flaskName.slice(-1)}`);
      if (!existingFlask) {
        deviceMap.get(deviceId)?.flasks.push({
          name: `O2 ${flaskName.slice(-1)}`,
          colour: color,
          visible: true,
          traceIndex,
        });
      }
    });

    const layout: Partial<Layout> = {
      width: this.width + this.margin.left + this.margin.right,
      height: this.height + this.margin.top + this.margin.bottom,
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: false,
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
      hoverlabel: {
        bgcolor: '#B4DFFF',
        bordercolor: '#0C7BB4',
        font: {
          family: 'figtree',
          size: 13,
        },
      },
      dragmode: 'zoom',
      hovermode: totalPoints > 50000 ? false : 'closest',
    };

    const config: Partial<Config> = {
      scrollZoom: true,
      displayModeBar: false,
      displaylogo: false,
      responsive: true,
      doubleClick: 'reset',
      staticPlot: false,
    };

    // Update legend data signal
    this.legendData.set(legendItems);

    try {
      Plotly.newPlot(this.plotlyDiv().nativeElement, traces, layout, config);
      this.graphReady = true;
      this.errorMessage = null;

      // Store initial axis ranges for zoom scale calculation
      const initialLayout = this.plotlyDiv().nativeElement.layout;
      this.initialXRange = initialLayout.xaxis?.range ? [...initialLayout.xaxis.range] as [number, number] : null;
      this.initialYRange = initialLayout.yaxis?.range ? [...initialLayout.yaxis.range] as [number, number] : null;

      // Handle layout changes (pan, zoom, etc.)
      this.plotlyDiv().nativeElement.on('plotly_relayout', () => {
        this.clearTooltips();
        this.updateMarkerSizeOnZoom();
      });
    } catch (error) {
      this.graphReady = false;
      this.errorMessage = 'Failed to render graph. Please try refreshing the page.';
    }
  };

  private updateGraph(): void {
    if (!this.plotlyDiv()?.nativeElement) return;

    // Recalculate min/max bounds from filtered data
    const { minY, maxY, minX, maxX } = this.getDataBounds(this.filteredDataToRender);

    const roundedMinY = parseFloat(minY.toFixed(4)) || 0;
    let roundedMaxY = parseFloat(maxY.toFixed(4)) || 1;
    const roundedMinX = parseFloat(minX.toFixed(4)) || 0;
    let roundedMaxX = parseFloat(maxX.toFixed(4)) || 1;

    if (roundedMinX === roundedMaxX) {
      roundedMaxX = roundedMinX + 1;
    }
    if (roundedMinY === roundedMaxY) {
      roundedMaxY = roundedMinY + 1;
    }

    this.advancedSettingsForm().patchValue({
      [this.xMinRangeField().key]: roundedMinX.toString(),
      [this.xMaxRangeField().key]: roundedMaxX.toString(),
      [this.yMinRangeField().key]: roundedMinY.toString(),
      [this.yMaxRangeField().key]: roundedMaxY.toString(),
    });

    // Group data by DeviceFlaskProperty and IsEarly
    const groupedData = new Map<string, IRenderData[]>();
    this.filteredDataToRender.forEach((point) => {
      const key = `${point.DeviceFlaskProperty}_${point.IsEarly ? 'early' : 'late'}`;
      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key)!.push(point);
    });

    // Determine if we should use WebGL
    const totalPoints = this.filteredDataToRender.length;
    const useWebGL = totalPoints > 5000;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    // Optimise marker size
    const markerSize = totalPoints > 50000 ? 4 : totalPoints > 10000 ? 6 : 8;
    this.baseMarkerSize = markerSize;

    // Create traces and rebuild custom legend data
    const seenDevices = new Set<string>();
    const traces: Data[] = [];
    const legendItems: ILegendDevice[] = [];
    const deviceMap = new Map<string, ILegendDevice>();

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const baseKey = key.replace(/_early$|_late$/, '');
      const label = this.labelList.find((l) => l.deviceFlaskProperty === baseKey);
      const color = label?.colour || '#000000';
      const deviceId = points[0]?.DeviceName || 'Unknown Device';
      const device = this.devices()?.find((d) => d.id.toString() === deviceId);
      const deviceName = device?.name || deviceId;
      const flaskName = points[0]?.FlaskName || key;
      const isEarly = points[0]?.IsEarly;

      // Track device for legend
      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const legendDevice: ILegendDevice = {
          deviceId,
          deviceName,
          flasks: [],
        };
        deviceMap.set(deviceId, legendDevice);
        legendItems.push(legendDevice);
      }

      const traceIndex = traces.length;

      // Add flask trace
      traces.push({
        x: points.map((p) => p.Time),
        y: points.map((p) => p.Value),
        mode: this.graphMode(),
        type: traceType,
        name: `${flaskName} (${isEarly ? 'Early' : 'Late'})`,
        marker: {
          color: color,
          size: this.getMarkerSize(markerSize, isEarly),
          symbol: this.getSymbol(isEarly),
        },
        line: {
          color: color,
          width: 1,
        },
        hoverlabel: {
          bgcolor: '#B4DFFF',
          bordercolor: '#0C7BB4',
          font: {
            family: 'figtree',
            size: 13,
          },
        },
        hovertemplate:
          '<b>Device:</b> %{customdata[0]}<br>' +
          '<b>Flask:</b> %{customdata[1]}<br>' +
          '<b>Type:</b> %{customdata[2]}<br>' +
          '<b>Time:</b> %{x}<br>' +
          '<b>Value:</b> %{y}' +
          '<extra></extra>',
        customdata: points.map((p) => [p.DeviceName, p.FlaskName, p.IsEarly ? 'Early' : 'Late']),
      } as Data);

      // Add to legend data
      const existingFlask = deviceMap.get(deviceId)?.flasks.find(f => f.name === `O2 ${flaskName.slice(-1)}`);
      if (!existingFlask) {
        deviceMap.get(deviceId)?.flasks.push({
          name: `O2 ${flaskName.slice(-1)}`,
          colour: color,
          visible: true,
          traceIndex,
        });
      }
    });

    const layout: Partial<Layout> = {
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: false,
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
    };

    // Update legend data signal
    this.legendData.set(legendItems);

    try {
      Plotly.react(this.plotlyDiv().nativeElement, traces, layout);
      this.errorMessage = null;
    } catch (error) {
      this.errorMessage = 'Failed to update graph with new data. Please try refreshing.';
    }
  }

  toggleLegendItem(deviceIndex: number, flaskIndex: number): void {
    this.legendData.update((items) => {
      const updatedItems = [...items];
      updatedItems[deviceIndex] = {
        ...updatedItems[deviceIndex],
        flasks: updatedItems[deviceIndex].flasks.map((flask, idx) =>
          idx === flaskIndex ? { ...flask, visible: !flask.visible } : flask,
        ),
      };
      return updatedItems;
    });

    const flask = this.legendData()[deviceIndex].flasks[flaskIndex];

    // Find all traces that match this flask (both early and late)
    const flaskLetter = flask.name.slice(-1);
    const tracesToToggle: number[] = [];

    this.plotlyDiv().nativeElement.data.forEach((trace: any, index: number) => {
      if (trace.name && trace.name.includes(flaskLetter)) {
        tracesToToggle.push(index);
      }
    });

    this.safePlotlyOperation(
      () => Plotly.restyle(
        this.plotlyDiv().nativeElement,
        {
          visible: flask.visible ? true : 'legendonly',
        },
        tracesToToggle,
      ),
      'Failed to toggle legend item. Please try again.'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.graphReady = false;
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.purge(this.plotlyDiv().nativeElement);
      } catch (error) {
        // Silently fail on cleanup
      }
    }
  }
}

export interface IRenderData {
  DeviceFlaskProperty: string;
  Time: number;
  Value: number;
  DeviceName: string;
  FlaskName: string;
  IsEarly: boolean;
}

export interface IPropertyColour {
  deviceFlaskProperty: string;
  colour: string;
}

export interface ILegendFlask {
  name: string;
  colour: string;
  visible: boolean;
  traceIndex: number;
}

export interface ILegendDevice {
  deviceId: string;
  deviceName: string;
  flasks: ILegendFlask[];
}
